import React, { useState, useEffect } from 'react';
import adminApi from '../utils/adminApi';
import { formatCurrency } from '../utils/formatters';
import '../styles/AdminUserManagement.css';

const AdminUserManagementTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userLogs, setUserLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editBalance, setEditBalance] = useState('');
  const [statusMessage, setStatusMessage] = useState({ text: '', type: '' });
  const [sortConfig, setSortConfig] = useState({ key: 'registered_at', direction: 'desc' });
  const [qrUsageData, setQrUsageData] = useState({});
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Fetching users from API...');
      const response = await adminApi.get('/api/admin/users');
      
      console.log(`Received ${response.data.length} users from API`);
      setUsers(response.data);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching users:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load users';
      const errorDetails = err.response?.data?.error || err.message;
      setError(`${errorMessage}: ${errorDetails}`);
      setLoading(false);
    }
  };
  
  const fetchUserLogs = async (userId) => {
    try {
      setLoadingLogs(true);

      // You'll need to create this endpoint on your server
      const response = await adminApi.get(`/api/admin/users/${userId}/logs`);
      setUserLogs(response.data);

      setLoadingLogs(false);
    } catch (err) {
      console.error(`Error fetching logs for user ${userId}:`, err);
      setUserLogs([]);
      setLoadingLogs(false);
    }
  };

  const fetchQrUsageData = async () => {
    try {
      const response = await adminApi.get('/api/admin/qr-usage');
      setQrUsageData(response.data);
    } catch (err) {
      console.error('Error fetching QR usage data:', err);
    }
  };

  const checkOnlineUsers = async () => {
    try {
      const response = await adminApi.get('/api/admin/online-users');
      setOnlineUsers(new Set(response.data.onlineUserIds));
    } catch (err) {
      console.error('Error checking online users:', err);
    }
  };

  const copyLoginCredentials = (user) => {
    const loginText = `Login ID: ${user.short_id}`;
    navigator.clipboard.writeText(loginText).then(() => {
      setStatusMessage({
        text: `Copied login credentials for ${user.short_id}`,
        type: 'success'
      });
      setTimeout(() => setStatusMessage({ text: '', type: '' }), 3000);
    }).catch(err => {
      console.error('Failed to copy:', err);
      setStatusMessage({
        text: 'Failed to copy credentials',
        type: 'error'
      });
    });
  };
  
  const updateUserBalance = async () => {
    if (!selectedUser || !editBalance) return;
    
    try {
      setStatusMessage({ text: 'Updating balance...', type: 'info' });
      
      // You'll need to create this endpoint on your server
      const response = await adminApi.post(`/api/admin/users/${selectedUser.id}/update-balance`, {
        newBalance: parseFloat(editBalance)
      });
      
      // Update the user in the local state
      setUsers(users.map(user => 
        user.id === selectedUser.id 
          ? { ...user, balance: parseFloat(editBalance) } 
          : user
      ));
      
      // Update selected user
      setSelectedUser({ ...selectedUser, balance: parseFloat(editBalance) });
      
      setStatusMessage({ text: 'Balance updated successfully', type: 'success' });
      setEditMode(false);
      
      // Clear status after 3 seconds
      setTimeout(() => setStatusMessage({ text: '', type: '' }), 3000);
    } catch (err) {
      console.error('Error updating user balance:', err);
      setStatusMessage({ 
        text: err.response?.data?.message || 'Failed to update balance', 
        type: 'error' 
      });
    }
  };
  
  const triggerMinigameForUser = (userId) => {
    // Set the active tab to minigame-trigger and pre-select this user
    // This requires coordination with the parent component
    // For now, we'll just show a message with instructions
    setStatusMessage({
      text: `To trigger a minigame for this user, go to the "Trigger Minigames" tab and select user ID: ${userId}`,
      type: 'info'
    });
    
    // Clear status after 5 seconds
    setTimeout(() => setStatusMessage({ text: '', type: '' }), 5000);
  };
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Fetch all users when component mounts
  useEffect(() => {
    fetchUsers();
    fetchQrUsageData();
    // Check online users immediately and then periodically
    checkOnlineUsers();
    const intervalId = setInterval(() => {
      checkOnlineUsers();
    }, 30000); // Check every 30 seconds

    return () => clearInterval(intervalId);
  }, []);

  // Fetch user logs when a user is selected
  useEffect(() => {
    if (selectedUser) {
      fetchUserLogs(selectedUser.id);
    }
  }, [selectedUser]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  
  const sortedUsers = [...users].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });
  
  const filteredUsers = sortedUsers.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (user.name && user.name.toLowerCase().includes(searchLower)) ||
      (user.short_id && user.short_id.toLowerCase().includes(searchLower)) ||
      (user.id && user.id.toLowerCase().includes(searchLower))
    );
  });
  
  const calculatePerformance = (initialBalance, currentBalance) => {
    if (!initialBalance || initialBalance === 0) return 0;
    return ((currentBalance - initialBalance) / initialBalance) * 100;
  };
  
  const getPerformanceClass = (performance) => {
    if (performance > 20) return 'high-gain';
    if (performance > 0) return 'gain';
    if (performance > -20) return 'loss';
    return 'high-loss';
  };
  
  return (
    <div className="admin-user-management">
      <h2>User Management</h2>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="user-management-container">
        <div className="user-list-section">
          <div className="search-and-filter">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button onClick={fetchUsers} className="refresh-button">
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="loading">Loading users...</div>
          ) : (
            <>
              <div className="user-count">
                {filteredUsers.length} users {searchTerm ? 'found' : 'total'}
              </div>

              <table className="users-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th onClick={() => handleSort('short_id')}>
                      ID {sortConfig.key === 'short_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('name')}>
                      Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('balance')}>
                      Balance {sortConfig.key === 'balance' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>QR Used</th>
                    <th onClick={() => handleSort('registered_at')}>
                      Registered {sortConfig.key === 'registered_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => {
                    const performance = calculatePerformance(user.initial_balance, user.balance);
                    const performanceClass = getPerformanceClass(performance);

                    return (
                      <tr
                        key={user.id}
                        className={`${selectedUser?.id === user.id ? 'selected-user' : ''} ${onlineUsers.has(user.id) ? 'user-online' : ''}`}
                        onClick={() => setSelectedUser(user)}
                      >
                        <td>
                          <span className={`status-dot ${onlineUsers.has(user.id) ? 'online' : 'offline'}`}>
                            {onlineUsers.has(user.id) ? '🟢' : '⚫'}
                          </span>
                        </td>
                        <td>{user.short_id}</td>
                        <td>{user.username || user.name || 'Anonymous'}</td>
                        <td className={performanceClass}>
                          {formatCurrency(user.balance)}
                          <span className="performance-indicator">
                            ({performance.toFixed(1)}%)
                          </span>
                        </td>
                        <td>
                          {qrUsageData[user.id] ? (
                            <span className="qr-count">{qrUsageData[user.id].count} QRs</span>
                          ) : (
                            <span className="qr-count">0</span>
                          )}
                        </td>
                        <td>{formatDate(user.registered_at)}</td>
                        <td>
                          <button
                            className="action-button copy-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyLoginCredentials(user);
                            }}
                            title="Copy login ID"
                          >
                            📋
                          </button>
                          <button
                            className="action-button view-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedUser(user);
                            }}
                          >
                            View
                          </button>
                          <button
                            className="action-button trigger-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              triggerMinigameForUser(user.id);
                            }}
                          >
                            Trigger
                          </button>
                          <button
                            className="action-button print-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`/badge-print/${user.id}`, '_blank');
                            }}
                            title="Print badge"
                          >
                            Print
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      {/* User Details Modal */}
      {selectedUser && (
        <div className="user-modal-overlay" onClick={() => { setSelectedUser(null); setEditMode(false); }}>
          <div className="user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="user-modal-header">
              <h3>{selectedUser.username || selectedUser.short_id}</h3>
              <button className="user-modal-close" onClick={() => { setSelectedUser(null); setEditMode(false); }}>&times;</button>
            </div>
            <div className="user-modal-body">
              <div className="user-details">
                <div className="detail-row">
                  <span className="detail-label">ID:</span>
                  <span className="detail-value">{selectedUser.short_id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Full ID:</span>
                  <span className="detail-value">{selectedUser.id}</span>
                </div>
                <div className="detail-row login-credentials">
                  <span className="detail-label">Login URL:</span>
                  <span className="detail-value">
                    <code>/register?id={selectedUser.short_id}</code>
                  </span>
                </div>
                {selectedUser.username && (
                  <div className="detail-row">
                    <span className="detail-label">Username:</span>
                    <span className="detail-value">
                      <code>{selectedUser.username}</code>
                    </span>
                  </div>
                )}
                {selectedUser.password && (
                  <div className="detail-row">
                    <span className="detail-label">Password:</span>
                    <span className="detail-value">
                      <code>{selectedUser.password}</code>
                    </span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">Quick Login Link:</span>
                  <span className="detail-value">
                    <a
                      href={`/login?auto=${selectedUser.short_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="quick-login-link"
                    >
                      Click to open login page
                    </a>
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Registered:</span>
                  <span className="detail-value">{formatDate(selectedUser.registered_at)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Jacket ID:</span>
                  <span className="detail-value">{selectedUser.jacket_id || 'None'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Initial Balance:</span>
                  <span className="detail-value">{formatCurrency(selectedUser.initial_balance)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Current Balance:</span>
                  {editMode ? (
                    <div className="edit-balance-controls">
                      <input
                        type="number"
                        value={editBalance}
                        onChange={(e) => setEditBalance(e.target.value)}
                        className="balance-input"
                      />
                      <button onClick={updateUserBalance} className="save-button">Save</button>
                      <button onClick={() => setEditMode(false)} className="cancel-button">Cancel</button>
                    </div>
                  ) : (
                    <>
                      <span className="detail-value">{formatCurrency(selectedUser.balance)}</span>
                      <button
                        onClick={() => {
                          setEditBalance(selectedUser.balance);
                          setEditMode(true);
                        }}
                        className="edit-button"
                      >
                        Edit
                      </button>
                    </>
                  )}
                </div>
                <div className="detail-row">
                  <span className="detail-label">Performance:</span>
                  <span className={`detail-value ${getPerformanceClass(calculatePerformance(selectedUser.initial_balance, selectedUser.balance))}`}>
                    {calculatePerformance(selectedUser.initial_balance, selectedUser.balance).toFixed(2)}%
                  </span>
                </div>
              </div>

              <div className="user-actions">
                <button
                  onClick={() => triggerMinigameForUser(selectedUser.id)}
                  className="trigger-minigame-button"
                >
                  Trigger Minigame
                </button>
                <button
                  onClick={() => {
                    const url = `${window.location.origin}/register?id=${selectedUser.short_id}`;
                    navigator.clipboard.writeText(url);
                    setStatusMessage({ text: 'Login URL copied to clipboard!', type: 'success' });
                    setTimeout(() => setStatusMessage({ text: '', type: '' }), 3000);
                  }}
                  className="copy-login-button"
                >
                  Copy Login URL
                </button>
                <button
                  onClick={() => window.open(`/badge-print/${selectedUser.id}`, '_blank')}
                  className="print-badge-button"
                >
                  Print Badge
                </button>
                <button
                  onClick={async () => {
                    const newPassword = prompt(`Reset password for ${selectedUser.username || selectedUser.short_id}.\n\nEnter new password (min 4 chars):`);
                    if (!newPassword) return;
                    if (newPassword.length < 4) {
                      setStatusMessage({ text: 'Password must be at least 4 characters', type: 'error' });
                      setTimeout(() => setStatusMessage({ text: '', type: '' }), 3000);
                      return;
                    }
                    try {
                      const response = await adminApi.post(`/api/admin/users/${selectedUser.id}/reset-password`, { newPassword });
                      setStatusMessage({ text: `Password reset for ${response.data.username}`, type: 'success' });
                      setTimeout(() => setStatusMessage({ text: '', type: '' }), 3000);
                    } catch (err) {
                      setStatusMessage({ text: err.response?.data?.message || 'Error resetting password', type: 'error' });
                      setTimeout(() => setStatusMessage({ text: '', type: '' }), 3000);
                    }
                  }}
                  className="reset-password-button"
                  style={{ background: '#f59e0b', color: '#000' }}
                >
                  Reset Password
                </button>
              </div>

              {qrUsageData[selectedUser.id] && (
                <div className="qr-usage-details">
                  <h4>QR Code Usage</h4>
                  <div className="detail-row">
                    <span className="detail-label">Total QR Codes Scanned:</span>
                    <span className="detail-value">{qrUsageData[selectedUser.id].count}</span>
                  </div>
                  {qrUsageData[selectedUser.id].details && (
                    <div className="qr-details-list">
                      {qrUsageData[selectedUser.id].details.map((qr, index) => (
                        <div key={index} className="qr-detail-item">
                          <span className="qr-type">{qr.type}</span>
                          <span className="qr-value">{qr.value}</span>
                          <span className="qr-time">{formatDate(qr.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <h4>Activity Logs</h4>
              {loadingLogs ? (
                <div className="loading">Loading logs...</div>
              ) : userLogs.length > 0 ? (
                <div className="user-logs">
                  <table className="logs-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Action</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userLogs.map((log, index) => (
                        <tr key={index}>
                          <td>{formatDate(log.timestamp)}</td>
                          <td>{log.action}</td>
                          <td>{log.details}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-logs">No activity logs available</div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {statusMessage.text && (
        <div className={`status-message ${statusMessage.type}`}>
          {statusMessage.text}
        </div>
      )}
    </div>
  );
};

export default AdminUserManagementTab; 